import { LitElement, html, css, nothing } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { ref, createRef } from "lit/directives/ref.js";
import { classMap } from "lit/directives/class-map.js";
import { animate } from "@lit-labs/motion";
import { SkyIcon } from "../sky-icon/sky-icon.js";
import { PresetController } from "../helper/controllers/preset-controller";

export type SkyDialogPadding = "none" | "xs" | "sm" | "md" | "lg" | "xl";
export type SkyDialogRequestCloseDetail = { reason: "backdrop" | "escape" | "close-button" };
/** Shared scroll lock state so multiple dialogs don't double-restore scroll. */
let _dialogOpenCount = 0;
let _savedScrollY = 0;

/**
 * @element sky-dialog
 *
 * @summary Accessible modal dialog built on native `<dialog>` with controlled open state, slots, and keyboard/backdrop handling.
 *
 * @status stable
 * @since 1.0.0
 *
 * @documentation https://sky-ui.com/components/dialog
 * @dependency sky-icon
 *
 * @uiVModel open request-close sync=constant:false
 *
 * @slot header - Header content area at the top of the dialog.
 * @slot body - Main content area of the dialog.
 * @slot footer - Footer actions area at the bottom of the dialog.
 *
 * @csspart dialog - The dialog surface wrapper element.
 * @csspart close-button - The top-right close button.
 * @csspart message - Optional message text shown above the body.
 * @csspart header - Container for the header slot.
 * @csspart content - Container for the body slot.
 * @csspart actions - Container for the footer slot.
 * @csspart footer - Alias part on the footer container.
 *
 * @fires {CustomEvent<SkyDialogRequestCloseDetail>} request-close - Fired when close intent is detected and parent should update `open`.
 * @fires {CustomEvent<void>} dialog-opened - Fired after open animation completes.
 * @fires {CustomEvent<void>} dialog-closed - Fired after close animation completes.
 *
 * @property {boolean} open - Whether the dialog is visible.
 * @property {boolean} closable - Shows a top-right close button.
 * @property {string} message - Optional message displayed above body content.
 * @property {boolean} overlay - Applies dim/blur backdrop effect.
 * @property {string} width - Max width of the dialog surface.
 * @property {boolean} separator - Shows separators around header/footer regions.
 * @property {boolean} persistent - Prevents outside/Escape close and highlights surface instead.
 * @property {boolean} closeOnEsc - Enables close intent on Escape key.
 * @property {SkyDialogPadding} headerPadding - Header padding token.
 * @property {SkyDialogPadding} bodyPadding - Body padding token.
 * @property {SkyDialogPadding} footerPadding - Footer padding token.
 * @property {boolean} highlightOnOutsideClick - Enables highlight animation on rejected outside click.
 * @property {string} preset - Named prop preset from nearest `sky-config-provider`. Default: `""`.
 * @method show Opens the dialog by setting `open` to true.
 * @method hide Closes the dialog by setting `open` to false.
 * @method focusDialog Focuses the native dialog surface.
 * @method getDialogElement Returns the native dialog element reference.
 *
 * @Behavior
 * - Uses native `<dialog>` element with enhanced functionality
 * - Controlled exclusively by the `open` attribute/property
 * - Uses native dialog focus behavior
 * - Supports smooth animations with configurable timing
 * - Uses the floating modal surface (frosted tertiary glass)
 * - **Standard**: Click outside closes dialog
 * - **Persistent**: Click outside highlights dialog instead of closing
 * - **Closable**: Shows close button in top-right corner
 * - **Escape handling**: Configurable Escape key behavior
 * - Uses `aria-modal="true"` for screen reader isolation
 * - Native dialog focus behavior
 * - Escape key handling with user preferences
 * - Close button with appropriate ARIA label
 * - Respects reduced motion preferences
 * - Smooth open/close animations with scale effects
 * - Highlight animation for persistent dialogs
 * - Configurable animation durations
 * - Uses Web Animations API for performance
 * - Works with any content in header/body/footer slots
 * - Compatible with form controls and interactive content
 * - Can be nested or stacked
 * - Responsive width and padding
 *
 * @example
 * ```html
 * <sky-dialog open closable width="420px" separator>
 *   <h3 slot="header">Confirm Action</h3>
 *   <p slot="body">Are you sure you want to proceed?</p>
 *   <div slot="footer">
 *     <button>Cancel</button>
 *     <button>Confirm</button>
 *   </div>
 * </sky-dialog>
 * ```
 * ```vue
 * <template>
 *   <sky-dialog open closable width="420px" separator>
 *     <h3 slot="header">Confirm Action</h3>
 *     <p slot="body">Are you sure you want to proceed?</p>
 *     <div slot="footer">
 *       <button>Cancel</button>
 *       <button>Confirm</button>
 *     </div>
 *   </sky-dialog>
 * </template>
 * ```
 * ```jsx
 * export default function Demo() {
 *   return (
 *     <sky-dialog open closable width="420px" separator>
 *       <h3 slot="header">Confirm Action</h3>
 *       <p slot="body">Are you sure you want to proceed?</p>
 *       <div slot="footer">
 *         <button>Cancel</button>
 *         <button>Confirm</button>
 *       </div>
 *     </sky-dialog>
 *   );
 * }
 * ```
 */
@customElement("sky-dialog")
export class SkyDialog extends LitElement {

  static dependencies: Record<string, CustomElementConstructor> = {
    "sky-icon": SkyIcon,
  };

  /** @public Whether the dialog is visible. */
  @property({ type: Boolean, reflect: true })
  open = false;

  /** @public Shows a top-right close button. */
  @property({ type: Boolean, reflect: true })
  closable = false;

  /** @public Optional message displayed above the body. */
  @property({ type: String })
  message = "";

  /** @public Applies dim/blur overlay on host. */
  @property({ type: Boolean, reflect: true })
  overlay = false;

  /** @public Max width of the dialog surface (e.g., "480px" or "auto"). */
  @property({ type: String })
  width = "auto";

  /** @public Shows header/footer separators. */
  @property({ type: Boolean, reflect: true })
  separator = false;

  /** @public Prevents close on outside click, highlights instead. */
  @property({ type: Boolean, reflect: true })
  persistent = false;

  /** @public Enables closing on Escape key. */
  @property({ type: Boolean, reflect: true })
  closeOnEsc = false;

  /** @public Padding token for header container. */
  @property({ type: String, reflect: true })
  headerPadding: SkyDialogPadding = "sm";

  /** @public Padding token for body container. */
  @property({ type: String, reflect: true })
  bodyPadding: SkyDialogPadding = "sm";

  /** @public Padding token for footer container. */
  @property({ type: String, reflect: true })
  footerPadding: SkyDialogPadding = "sm";


  /** @public Highlights dialog on outside click instead of closing. */
  @property({
    type: Boolean,
    reflect: true,
  })
  highlightOnOutsideClick = true;

  /** @private Whether header slot has content. */
  @state()
  private _hasHeader = false;

  /** @private Whether body slot has content. */
  @state()
  private _hasBody = false;

  /** @private Whether footer slot has content. */
  @state()
  private _hasFooter = false;

  /** @private Whether dialog is in closing animation. */
  @state()
  private _isClosing = false;

  /** @private Open animation instance. */
  private _openAnimation: Animation | null = null;

  /** @private Close animation instance. */
  private _closeAnimation: Animation | null = null;

  /** @private Highlight animation instance. */
  private _highlightAnimation: Animation | null = null;

  /** @private Reference to the native dialog element. */
  @query("dialog")
  private dialogElement?: HTMLDialogElement;

  /** @public Ref for the dialog element (e.g. for focus trap or positioning). */
  dialogRef = createRef<HTMLDialogElement>();

  /** @private Handle clicks on the dialog backdrop (outside surface). */
  private _onBackdropPointerDown = (event: PointerEvent): void => {
    const dialog = this.dialogElement;
    if (!dialog || !dialog.open) return;

    // In the <dialog> element, backdrop clicks are retargeted to the dialog itself.
    // Clicks inside the surface target inner elements.
    if (event.target !== dialog) return;

    // "Persistent" dialogs don't close via outside click; they highlight instead (optional).
    if (this.persistent) {
      this._highlightDialog();
      return;
    }

    // Standard behavior: outside click requests close.
    this._requestClose("backdrop");
  };


  /** Named prop preset from nearest `sky-config-provider`. */
  @property({ type: String, reflect: true }) preset = "";

  private _presets = new PresetController(this);

  static override styles = css`
    :host {
      display: contents;
      font-family: var(--sky-font);
    }

    /* Floating modal template (§20.3) — single canonical surface.
       Do not set display here: author display overrides UA
       dialog:not([open]) { display: none } and the closed dialog stays visible. */
    dialog {
      padding: 0;
      margin: auto;
      color: var(--sky-text-primary);
      position: fixed;
      box-sizing: border-box;
      width: 100%;
      text-align: center;
      overflow: hidden;
      z-index: var(--sky-z-modal, 1300);
      background: var(--sky-glass-tertiary);
      background-image: var(--sky-glass-surface);
      backdrop-filter: var(--sky-blur-tertiary) var(--sky-saturation-tertiary)
        var(--sky-brightness-tertiary);
      -webkit-backdrop-filter: var(--sky-blur-tertiary)
        var(--sky-saturation-tertiary) var(--sky-brightness-tertiary);
      border: var(--sky-border-tertiary);
      border-radius: var(--sky-radius-tertiary);
      box-shadow:
        var(--sky-box-shadow-inset),
        var(--sky-box-shadow-tertiary);
    }

    dialog[open] {
      display: flex;
      flex-direction: column;
    }

    dialog::backdrop {
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
      transition: backdrop-filter 0.3s ease-in-out;
    }

    :host([overlay]) dialog::backdrop {
      backdrop-filter: var(--sky-blur-tertiary);
      -webkit-backdrop-filter: var(--sky-blur-tertiary);
    }

    .header-padding-none,
    .body-padding-none,
    .footer-padding-none {
      padding: var(--sky-space-0, 0);
    }

    .header-padding-xs,
    .body-padding-xs,
    .footer-padding-xs {
      padding: var(--sky-space-1, 4px);
    }

    .header-padding-sm,
    .body-padding-sm,
    .footer-padding-sm {
      padding: var(--sky-space-2, 8px);
    }

    .header-padding-md,
    .body-padding-md,
    .footer-padding-md {
      padding: var(--sky-space-4, 16px);
    }

    .header-padding-lg,
    .body-padding-lg,
    .footer-padding-lg {
      padding: var(--sky-space-6, 24px);
    }

    .header-padding-xl,
    .body-padding-xl,
    .footer-padding-xl {
      padding: var(--sky-space-8, 32px);
    }

    /* Close button — §20.6 inner dismiss affordance */
    .close-button {
      position: absolute;
      top: var(--sky-space-1\\.5, 6px);
      right: var(--sky-space-1\\.5, 6px);
      display: flex;
      align-items: center;
      justify-content: center;
      width: var(--sky-space-6, 24px);
      height: var(--sky-space-6, 24px);
      box-sizing: border-box;
      border-radius: var(--sky-radius-full);
      font-size: var(--sky-font-size-sm, 14px);
      cursor: pointer;
      color: var(--sky-text-primary);
      border: none;
      background-color: var(--sky-hover-primary);
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
      transition: color 0.2s ease, background-color 0.2s ease;
      z-index: 1;
    }

    .close-button sky-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
    }

    .close-button:hover {
      color: var(--sky-danger-primary);
    }

    .close-button:focus-visible {
      outline: none;
      box-shadow: var(--sky-focus-ring);
    }

    /* Message */
    .message {
      padding: var(--sky-space-4, 16px) 0;
      margin: 0;
    }

    ::slotted([slot="header"]) {
      font-size: var(--sky-font-size-md, 16px);
      font-weight: var(--sky-font-weight-bold, 700);
      color: var(--sky-text-primary);
      box-sizing: border-box;
    }

    dialog:focus,
    dialog:focus-visible {
      outline: none;
    }

    .header-separator {
      border-bottom: var(--sky-border-primary);
    }

    .footer-separator {
      border-top: var(--sky-border-primary);
    }

    sky-icon {
      display: inline-block;
      width: 1em;
      height: 1em;
    }

    /* RTL support */
    :host([dir="rtl"]) .close-button {
      right: auto;
      left: var(--sky-space-1\\.5, 6px);
    }

    /* Animation classes */
    dialog.opening {
      opacity: 0;
    }

    dialog.closing {
      opacity: 1;
    }

    @media (prefers-reduced-motion: reduce) {
      dialog::backdrop,
      .close-button {
        transition: none;
      }
    }
  `;

  constructor() {
    super();
    this._handleCancel = this._handleCancel.bind(this);
  }

  /** @protected */
  override firstUpdated() {
    const d = this.dialogElement;
    if (!d) return;

    // Capture phase is important so we beat the native <dialog> cancel/close behavior
    d.addEventListener("keydown", this._onKeyDown, { capture: true });
    d.addEventListener("pointerdown", this._onBackdropPointerDown);
    d.addEventListener("cancel", this._handleCancel as any); // optional extra safety
    d.addEventListener("close", this._onNativeClose); // keep state in sync (below)
  }

  /** @protected */
  override disconnectedCallback() {
    const d = this.dialogElement;
    if (d) {
      d.removeEventListener("keydown", this._onKeyDown, {
        capture: true,
      } as any);
      d.removeEventListener("pointerdown", this._onBackdropPointerDown);
      d.removeEventListener("cancel", this._handleCancel as any);
      d.removeEventListener("close", this._onNativeClose);
    }
    super.disconnectedCallback();
    this._unlockScroll();
    if (d?.open) d.close();
  }

  /**
   * Opens the dialog programmatically.
   * @public
   */
  public show(): void {
    this.open = true;
  }

  /**
   * Closes the dialog programmatically.
   * @public
   */
  public hide(): void {
    this.open = false;
  }

  /**
   * Manually focuses the dialog element.
   * @public
   */
  public focusDialog(): void {
    if (this.dialogElement) {
      this.dialogElement.focus();
    }
  }

  /**
   * Gets the native dialog element.
   * @returns The native HTMLDialogElement or undefined
   * @public
   */
  public getDialogElement(): HTMLDialogElement | undefined {
    return this.dialogElement;
  }

  /** @private */
  private _onNativeClose = (): void => {
    // If parent still says open=true, reopen immediately (persistent behavior)
    if (this.open) {
      // restore modal state
      requestAnimationFrame(() => {
        if (!this.dialogElement?.open) {
          this.dialogElement?.showModal();
        }
      });
    }
  };

  /** @protected */
  override updated(changedProps: Map<string, any>) {
    if (changedProps.has("open")) {
      // Use requestAnimationFrame to ensure DOM updates are complete
      requestAnimationFrame(() => {
        this._handleOpenChange(changedProps.get("open"));
      });
    }
  }

  /**
   * Handles open state changes.
   * @param previousOpen - Previous open state
   * @private
   */
  private _handleOpenChange(previousOpen: boolean): void {
    if (!this.dialogElement) return;

    if (this.open && !this.dialogElement.open) {
      this._lockScroll();
      this.dialogElement.showModal();

      requestAnimationFrame(() => {
        this._animateOpen();
      });
    } else if (!this.open && previousOpen) {
      this._isClosing = true;
      this._animateClose();
    }
  }

  /** @private */
  private _lockScroll(): void {
    if (typeof window === "undefined" || typeof document === "undefined") return;
    if (_dialogOpenCount === 0) {
      _savedScrollY = window.scrollY;
      window.scrollTo(0, 0);
    }
    _dialogOpenCount++;
    document.documentElement.setAttribute("data-sky-dialog-open", "true");
  }

  /** @private */
  private _unlockScroll(): void {
    if (typeof window === "undefined" || typeof document === "undefined") return;
    _dialogOpenCount = Math.max(0, _dialogOpenCount - 1);
    if (_dialogOpenCount === 0) {
      document.documentElement.removeAttribute("data-sky-dialog-open");
      window.scrollTo(0, _savedScrollY);
    }
  }

  /** @private */
  private _prefersReducedMotion(): boolean {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  /** @private */
  private _animateOpen(): void {
    const dialog = this.dialogElement;
    if (!dialog) return;

    this._openAnimation?.cancel();
    this._closeAnimation?.cancel();

    const duration = this._prefersReducedMotion() ? 0 : 300;
    const animation = dialog.animate(
      [
        {
          opacity: 0,
          transform: "scale(0.95)",
          transformOrigin: "center center",
        },
        { opacity: 1, transform: "scale(1)", transformOrigin: "center center" },
      ],
      {
        duration,
        easing: "ease-out",
        fill: "forwards",
      },
    );

    animation.onfinish = () => {
      this.dispatchEvent(
        new CustomEvent("dialog-opened", {
          bubbles: true,
          composed: true,
        }),
      );
    };

    this._openAnimation = animation;
  }

  /** @private */
  private _animateClose(): void {
    const dialog = this.dialogElement;
    if (!dialog) return;
    this._openAnimation?.cancel();
    this._closeAnimation?.cancel();

    const duration = this._prefersReducedMotion() ? 0 : 150;
    const animation = dialog.animate(
      [
        { opacity: 1, transform: "scale(1)", transformOrigin: "center center" },
        {
          opacity: 0,
          transform: "scale(0.95)",
          transformOrigin: "center center",
        },
      ],
      {
        duration,
        easing: "ease-out",
        fill: "forwards",
      },
    );

    animation.onfinish = () => {
      dialog.close();
      this._unlockScroll();
      this._isClosing = false;
      this.dispatchEvent(
        new CustomEvent("dialog-closed", {
          bubbles: true,
          composed: true,
        }),
      );
    };

    this._closeAnimation = animation;
  }

  /** @private */
  private _handleCancel(event: Event): void {
    event.preventDefault();

    if (this.persistent || !this.closeOnEsc) {
      this._highlightDialog();
      return;
    }

    this._requestClose("escape");
  }

  /** @private */
  private _highlightDialog(): void {
    if (!this.highlightOnOutsideClick) return;
    if (this._prefersReducedMotion()) return;

    const dialog = this.dialogElement;
    if (!dialog) return;

    this._highlightAnimation?.cancel();

    const animation = dialog.animate(
      [
        { transform: "scale(1)" },
        { transform: "scale(1.05)" },
        { transform: "scale(1)" },
      ],
      {
        duration: 300,
        easing: "ease-in-out",
      },
    );

    this._highlightAnimation = animation;
  }



  /** @private */
  private _requestClose(reason: SkyDialogRequestCloseDetail["reason"]): void {
    if (this._isClosing) return;
    this.dispatchEvent(
      new CustomEvent<SkyDialogRequestCloseDetail>("request-close", {
        detail: { reason },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * Checks if a slot has content.
   * @param slot - Slot element to check
   * @returns True if slot has content
   * @private
   */
  private _slotHasContent(slot: HTMLSlotElement): boolean {
    const nodes = slot.assignedNodes({ flatten: true });
    return nodes.some((n) => {
      if (n.nodeType === Node.TEXT_NODE) {
        return !!n.textContent?.trim();
      }
      return true;
    });
  }

  /** @private */
  private _onHeaderSlotChange = (e: Event): void => {
    const slot = e.target as HTMLSlotElement;
    this._hasHeader = this._slotHasContent(slot);
  };

  /** @private */
  private _onBodySlotChange = (e: Event): void => {
    const slot = e.target as HTMLSlotElement;
    this._hasBody = this._slotHasContent(slot);
  };

  /** @private */
  private _onFooterSlotChange = (e: Event): void => {
    const slot = e.target as HTMLSlotElement;
    this._hasFooter = this._slotHasContent(slot);
  };

  /** @private */
  private _onKeyDown = (e: KeyboardEvent): void => {
    if (e.key !== "Escape") return;

    // Always stop native <dialog> ESC behavior
    e.preventDefault();
    e.stopPropagation();
    // If persistent OR closeOnEsc is false → do NOT close, just highlight
    if (this.persistent || !this.closeOnEsc) {
      this._highlightDialog();
      return;
    }

    // Allowed to close via ESC
    this._requestClose("escape");
  };

  /** @protected */
  override render() {
    const valid: Record<SkyDialogPadding, true> = {
      none: true,
      xs: true,
      sm: true,
      md: true,
      lg: true,
      xl: true,
    };

    const hp = valid[this.headerPadding] ? this.headerPadding : "md";
    const bp = valid[this.bodyPadding] ? this.bodyPadding : "md";
    const fp = valid[this.footerPadding] ? this.footerPadding : "md";

    const headerClasses: Record<string, boolean> = {};
    if (this._hasHeader) {
      headerClasses[`header-padding-${hp}`] = true;
      if (this.separator) {
        headerClasses["header-separator"] = true;
      }
    }

    const footerClasses: Record<string, boolean> = {};
    if (this._hasFooter) {
      footerClasses["actions"] = true;
      footerClasses[`footer-padding-${fp}`] = true;
      if (this.separator) {
        footerClasses["footer-separator"] = true;
      }
    } else {
      footerClasses["actions"] = true;
    }

    return html`
      <dialog
        part="dialog"
        aria-modal="true"
        style="max-width: ${this.width};"
        @cancel="${this._handleCancel}"
        ${ref(this.dialogRef)}
        ${animate({
          keyframeOptions: {
            duration: 300,
            easing: "ease-out",
          },
        })}
      >
        <slot></slot>

        ${when(this.message, () => html`
          <p class="message" part="message">${this.message}</p>
        `)}

        <div class=${classMap(headerClasses)} part="header">
          <slot name="header" @slotchange=${this._onHeaderSlotChange}></slot>
        </div>

        <div
          class=${classMap({
            [`body-padding-${bp}`]: this._hasBody,
          })}
          part="content"
        >
          <slot name="body" @slotchange=${this._onBodySlotChange}></slot>
        </div>

        <div class=${classMap(footerClasses)} part="actions footer">
          <slot name="footer" @slotchange=${this._onFooterSlotChange}></slot>
        </div>
        ${when(this.closable, () => html`
          <button
            class="close-button"
            part="close-button"
            type="button"
            aria-label="Close dialog"
            @click=${() => this._requestClose("close-button")}
          >
            <sky-icon icon="ion:close"></sky-icon>
          </button>
        `)}
      </dialog>
    `;
  }
}
